import React from 'react';
import {withStyles} from '@material-ui/core/styles';
import {RichText} from "prismic-reactjs";
import {DefaultPlayer} from '@gridhaus/react-html5video';
import {standardContainer} from "../styles/mixins";
import Slice from "./Slice";
import Container from "./Container";
import Quote from "./Quote";
import Figure from "./Figure";
import ImageGrid from "./ImageGrid";
import classNames from 'classnames';


const styles = (theme) => ({
  container: {
    ...standardContainer(theme),
  },
  standardSlice: {
    marginTop: theme.spacing.unit * 4,
    marginBottom: theme.spacing.unit * 4,
    [theme.breakpoints.up(1024)]: {
      marginTop: theme.spacing.unit * 6,
      marginBottom: theme.spacing.unit * 6,
    },
    '&:first-child': {
      marginTop: 0,
    },
    '&:last-child': {
      marginBottom: 0,
    },
  },
  wideSlice: {
    marginBottom: theme.spacing.unit * 4,
    '&:last-child': {
      marginBottom: 0,
    }
  },
  contentMargins: {
    [theme.breakpoints.up(1024)]: {
      marginTop: theme.spacing.unit * -2,
      marginBottom: theme.spacing.unit * -2,
    },
  },
  brief: {
    maxWidth: theme.layout.contentMaxWidth,
    margin: '0 auto',
    '& h2': {
      ...theme.typography.hg36l(theme),
      marginTop: theme.spacing.unit * 6,
      marginBottom: theme.spacing.unit * 2,
      '&:first-child': {
        marginTop: 0,
      },
    },
    '& h3': {
      ...theme.typography.hg18bu(theme),
      marginTop: theme.spacing.unit * 4,
      marginBottom: theme.spacing.unit * 2,
    },
    '& p': {
      ...theme.typography.hg20r(theme),
      marginBottom: theme.spacing.unit * 2,
    },
    '& p:last-child': {
      marginBottom: 0,
    },
    '& ul, & ol': {
      marginBottom: theme.spacing.unit * 2,
      listStyle: 'none',
      ...theme.typography.hg20r(theme),
      '& li': {
        marginLeft: '1em',
        marginBottom: theme.spacing.unit * 1,
        '&::before': {
          content: "'\u2022'",
          position: 'absolute',
          marginLeft: '-1em',
        },
      },
    },
    '& p.block-img': {
      margin: 0,
    },
    '& img': {
      marginTop: theme.spacing.unit * 4,
      marginBottom: theme.spacing.unit * 4,
      maxWidth: '100%',
      display: 'block',
    },
    '& strong': {
      fontWeight: 'bold',
    },
    '& em': {
      fontStyle: 'italic',
    },
    '& a': {
      color: theme.palette.primary.main,
    }
  },
  quote: {
    maxWidth: theme.layout.contentMaxWidth,
  },
  captions: {
    textAlign: 'center',
    ...theme.typography.hg14b(theme),
    color: theme.palette.secondary.light,
    margin: `${theme.spacing.unit}px 0`,
  },
  image: {
    width: '100%',
  },
  smallImage: {
    maxWidth: theme.layout.contentMaxWidth,
    margin: '0 auto',
  },
  wideImage: {
    width: '100%',
  },
  imageGrid: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    margin: `-${theme.spacing.unit}px -${theme.spacing.unit}px`,
  },
  imageGridItem: {
    '& img': {
      maxWidth: '100%',
    },
    padding: `${theme.spacing.unit}px ${theme.spacing.unit}px`,
    [theme.breakpoints.up('md')]: {
      flexBasis: '50%',
    }
  },
  video: {
    margin: 0,
    padding: 0,
    '& video': {
      width: '100%',
      display: 'block',
    }
  },
  separator: {
    maxWidth: theme.layout.contentMaxWidth,
    border: 0,
    height: 2,
    background: theme.palette.primary.main,
  },
});


function ArticleBody(props) {
  const {slices, classes} = props;

  return (
    <React.Fragment>
      {slices.map((slice, index) => {
        switch (slice.slice_type) {
          case 'brief':
            return (
              <Slice key={index}
                     className={classes.standardSlice}
                     style={{
                       backgroundColor: slice.primary.background_color,
                       color: slice.primary.color
                     }}>
                <div className={classes.container}>
                  <div key={index} className={classes.brief}>
                    {RichText.render(slice.primary.brief)}
                  </div>
                </div>
              </Slice>
            );
          case 'body':
            return (
              <Slice key={index}
                     className={classes.standardSlice}
                     style={{
                       backgroundColor: slice.primary.background_color,
                       color: slice.primary.color
                     }}>
                <div className={classes.container}>
                  <div key={index} className={classes.brief}>
                    {RichText.render(slice.primary.brief)}
                  </div>
                </div>
              </Slice>
            );
          case 'quote':
            return (
              <Slice className={classes.standardSlice} key={index}>
                <Container>
                  <Quote className={classes.quote}
                         textMaxWidth={640}
                         text={RichText.render(slice.primary.text)}/>
                </Container>
              </Slice>
            );
          case 'image':
            return (
              <Slice key={index} className={`${classes.standardSlice} ${slice.primary.size === 'small' ? classes.contentMargins : ''}`}>
                <div className={classes.container}>
                  <Figure
                    imageSrc={slice.primary.image.url}
                    imageAlt={slice.primary.image.alt}
                    width={slice.primary.image.dimensions.width}
                    height={slice.primary.image.dimensions.height}
                    caption={slice.primary.show_captions === 'Yes' && slice.primary.image.alt}
                    className={slice.primary.size === 'small' ? classes.smallImage : ''}
                  />
                </div>
              </Slice>
            );
          case 'wide_image':
            return (
              <Slice wide key={index} className={classes.standardSlice}>
                <Figure imageSrc={slice.primary.image.url}
                        imageAlt={slice.primary.image.alt}
                        width={slice.primary.image.dimensions.width}
                        height={slice.primary.image.dimensions.height}
                        caption={slice.primary.show_captions === 'Yes' && slice.primary.image.alt}
                />
              </Slice>
            );
          case 'image_grid':
            return (
              <Slice key={index} className={classes.standardSlice}>
                <div className={classes.container}>
                  <ImageGrid>
                    {slice.items.map((item, index) => (
                      <Figure key={index}
                              width={item.image.dimensions.width}
                              height={item.image.dimensions.height}
                              imageSrc={item.image.url}
                              imageAlt={item.image.alt}/>
                    ))}
                  </ImageGrid>
                </div>
              </Slice>
            );
          case 'video':
            if (slice.primary.wide === 'Yes') {
              return (
                <div key={index} className={classes.wideSlice}>
                  <DefaultPlayer className={classes.video} autoPlay loop controls={null} muted>
                    <source src={slice.primary.video.url}/>
                    Sorry, your browser doesn't support embedded videos.
                  </DefaultPlayer>
                </div>
              );
            } else {
              return (
                <Slice key={index} className={classes.standardSlice}>
                  <div className={classes.container}>
                    <DefaultPlayer className={classes.video} autoPlay loop controls={null} muted>
                      <source src={slice.primary.video.url}/>
                      Sorry, your browser doesn't support embedded videos.
                    </DefaultPlayer>
                  </div>
                </Slice>
              );
            }
          case 'separator':
            return (
              <Slice key={index} className={classes.standardSlice}>
                <Container>
                  <hr className={classes.separator}/>
                </Container>
              </Slice>
            );
          default:
            return null;
        }
      })}
    </React.Fragment>
  );
}

export default withStyles(styles)(ArticleBody);


// WEBPACK FOOTER //
// ./src/parts/ArticleBody.js